/**
 * Read-only summary of the enrolled customer.
 * Replaces the form on the left column after a successful enrollment so
 * the admin can see what was created without the impression they can edit.
 */

import {
  Building2,
  Palette,
  UserRound,
  Globe,
  Hash,
  Mail,
  Phone,
  Sliders,
  Settings2
} from "lucide-react"
import { PRICING_ENGINE_LABELS } from "../../../types/pricing-engine"
import { EnrollFormValues } from "./enroll-form"
import { EnrollmentResponse } from "../types"

interface Props {
  values: EnrollFormValues
  enrollment: EnrollmentResponse
}

const HEX_RX = /^#[0-9a-fA-F]{6}$/

const Field = ({
  label,
  value,
  icon,
  mono,
  href
}: {
  label: string
  value: string
  icon?: React.ReactNode
  mono?: boolean
  href?: string
}) =>
  value ? (
    <div className="flex items-start gap-3 py-2">
      {icon && <span className="mt-0.5 text-neutral-400">{icon}</span>}
      <div className="min-w-0">
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-400">
          {label}
        </div>
        {href ? (
          <a
            href={href}
            className={`mt-0.5 text-sm text-primary-500 hover:text-primary-600 break-all ${
              mono ? "font-mono" : ""
            }`}
          >
            {value}
          </a>
        ) : (
          <div
            className={`mt-0.5 text-sm text-neutral-900 break-all ${
              mono ? "font-mono" : ""
            }`}
          >
            {value}
          </div>
        )}
      </div>
    </div>
  ) : null

interface SectionProps {
  index: string
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}

const Section = ({ index, title, icon, children }: SectionProps) => (
  <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
    <header className="flex items-start gap-4 px-6 pt-6 pb-4 border-b border-neutral-100">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">
            {index}
          </span>
          <h2 className="font-serif text-lg text-neutral-900">{title}</h2>
        </div>
      </div>
    </header>
    <div className="px-6 py-5 space-y-1">{children}</div>
  </section>
)

const EnrollmentSummary = ({ values, enrollment }: Props) => {
  const engineLabel =
    values.api_mode && PRICING_ENGINE_LABELS[values.api_mode]
      ? PRICING_ENGINE_LABELS[values.api_mode]
      : values.api_mode || null
  const orgName = values.org_name?.trim()

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-5 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white text-sm">
          ✓
        </span>
        <div>
          <div className="text-sm font-medium text-emerald-900">
            {values.company_name} has been enrolled
          </div>
          <div className="text-xs text-emerald-700">{enrollment.message}</div>
        </div>
      </div>

      {/* Company */}
      <Section
        index="01 / Company"
        title={values.company_name || "Company"}
        icon={<Building2 size={20} />}
      >
        <Field label="Company name" value={values.company_name} />
        {orgName && orgName !== values.company_name && (
          <Field label="Org name" value={orgName} />
        )}
        <Field
          label="NMLS"
          value={values.company_nmls}
          icon={<Hash size={14} />}
          mono
        />
        <Field
          label="Website"
          value={values.website}
          icon={<Globe size={14} />}
        />
        {engineLabel && (
          <Field
            label="Pricing engine"
            value={engineLabel}
            icon={<Sliders size={14} />}
          />
        )}
        {enrollment.org_id && (
          <Field
            label="Org"
            value={`#${enrollment.org_id}`}
            mono
            href={`/organizations/${enrollment.org_id}`}
          />
        )}
        {enrollment.brand_id && (
          <Field
            label="Brand"
            value={`#${enrollment.brand_id}`}
            mono
            href={`/brands/${enrollment.brand_id}`}
          />
        )}
      </Section>

      {/* Branding */}
      {(values.primary_color || values.secondary_color || values.logo_url) && (
        <Section
          index="02 / Branding"
          title="Brand appearance"
          icon={<Palette size={20} />}
        >
          {(values.primary_color || values.secondary_color) && (
            <div className="flex items-center gap-4 py-2">
              <span className="text-[11px] font-mono uppercase tracking-widest text-neutral-400">
                Colors
              </span>
              <div className="flex items-center gap-2">
                {values.primary_color && HEX_RX.test(values.primary_color) && (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="h-6 w-6 rounded border border-neutral-200"
                      style={{ backgroundColor: values.primary_color }}
                    />
                    <span className="text-xs font-mono text-neutral-600">
                      {values.primary_color.toUpperCase()}
                    </span>
                  </div>
                )}
                {values.secondary_color && HEX_RX.test(values.secondary_color) && (
                  <div className="flex items-center gap-1.5">
                    <div
                      className="h-6 w-6 rounded border border-neutral-200"
                      style={{ backgroundColor: values.secondary_color }}
                    />
                    <span className="text-xs font-mono text-neutral-600">
                      {values.secondary_color.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {values.logo_url && (
            <div className="py-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-400 mb-2">
                Logo
              </div>
              <div className="h-16 w-28 rounded-lg border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden">
                <img
                  src={values.logo_url}
                  alt="Company logo"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Brand settings */}
      {(() => {
        const bs = values.brand_settings
        const entries = bs
          ? Object.entries(bs).filter(([, v]) =>
              typeof v === "boolean" ? v : !!v
            )
          : []
        if (entries.length === 0) return null
        return (
          <Section
            index="03 / Brand settings"
            title="Advanced brand config"
            icon={<Settings2 size={20} />}
          >
            {entries.map(([key, val]) => (
              <Field
                key={key}
                label={key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, c => c.toUpperCase())}
                value={
                  typeof val === "boolean" ? (val ? "Yes" : "No") : String(val)
                }
              />
            ))}
          </Section>
        )
      })()}

      {/* First admin user */}
      <Section
        index="04 / First admin"
        title={
          `${values.first_user.first_name} ${values.first_user.last_name}`.trim() ||
          "Admin user"
        }
        icon={<UserRound size={20} />}
      >
        <Field
          label="Name"
          value={`${values.first_user.first_name} ${values.first_user.last_name}`.trim()}
        />
        <Field
          label="Email"
          value={values.first_user.email}
          icon={<Mail size={14} />}
        />
        <Field
          label="NMLS"
          value={values.first_user.nmls}
          icon={<Hash size={14} />}
          mono
        />
        {values.first_user.phone && (
          <Field
            label="Phone"
            value={values.first_user.phone}
            icon={<Phone size={14} />}
          />
        )}
        {enrollment.loid && (
          <Field
            label="Loan officer"
            value={`#${enrollment.loid}`}
            mono
            href={`/loan-officers/${enrollment.loid}`}
          />
        )}
      </Section>
    </div>
  )
}

export default EnrollmentSummary
